/**
 * Seguimiento de la solicitud de re-vinculación (T039; spec US3, escenarios 2–4).
 *
 * Consulta `GET /rebind-requests/{ref}` cada 5 s mientras siga `PENDING` (la referencia es opaca:
 * también responde `PENDING` si no existe, así que esta pantalla es idéntica para un documento
 * inexistente). En cuanto el admin aprueba:
 *
 * - `APPROVED` → `POST /rebind-requests/{ref}/claim`, firmado con la clave que se envió en la
 *   solicitud y **sin** `id=` (el servidor deriva el `device_id`). El `200` trae la cookie: el
 *   trabajador entra a Inicio sin invitación nueva (FR de US3).
 * - `REJECTED` / `EXPIRED` → mensaje y vuelta; se olvida la referencia guardada.
 *
 * Errores del `claim`: `401 DEVICE_MISMATCH` (la credencial del teléfono cambió desde la solicitud)
 * y `409 REBIND_NOT_PENDING` (ya reclamada o resuelta) ofrecen enviar una solicitud nueva.
 */
import { useEffect, useRef } from 'react';

import { Button } from '../../components/ui/button';
import { Screen } from '../../components/ui/screen';
import { SpinnerRing } from '../../components/ui/spinner-ring';
import { t } from '../../i18n';

import { useClaimRebind, useRebindStatus } from './rebind-api';
import { clearRebindRequest } from './rebind-store';

import type { ApiError } from '../../api/client';
import type { ReactNode } from 'react';

export interface RebindStatusScreenProps {
  /** `request_ref` opaco (`rbq_…`) que devolvió el `202`. */
  requestRef: string;
  /** `claim` correcto: ya hay sesión en este teléfono. */
  onClaimed: () => void;
  /** "Volver" → pantalla de dispositivo no registrado. */
  onBack: () => void;
  /** "Solicitar de nuevo" → formulario de documento. */
  onRequestAgain: () => void;
}

export function claimErrorMessage(error: ApiError): string {
  if (error.is('DEVICE_MISMATCH') || error.is('DEVICE_CLOCK_SKEW')) {
    return t('access.rebind.status.claimMismatch');
  }
  if (error.is('REBIND_NOT_PENDING')) {
    return t('access.rebind.status.notPending');
  }
  return t('access.rebind.status.claimError');
}

function WaitingScreen({
  label,
  title,
  lead,
  footer,
}: {
  label: string;
  title: string;
  lead: string;
  footer?: ReactNode;
}) {
  return (
    <Screen data-testid="rebind-status" align="center" footer={footer}>
      <div className="flex flex-col items-center gap-fm-4 text-center">
        <SpinnerRing label={label} />
        <h1 className="font-display text-fm-2xl font-extrabold text-fm-text">{title}</h1>
        <p aria-live="polite" className="text-fm-base text-fm-text-secondary">
          {lead}
        </p>
      </div>
    </Screen>
  );
}

export function RebindStatusScreen({
  requestRef,
  onClaimed,
  onBack,
  onRequestAgain,
}: RebindStatusScreenProps) {
  const status = useRebindStatus(requestRef);
  const claim = useClaimRebind(requestRef);
  const claimStarted = useRef(false);

  const current = status.data?.status;

  // Aprobada: se reclama una sola vez (un segundo `claim` responde 409).
  useEffect(() => {
    if (current !== 'APPROVED' || claimStarted.current) {
      return;
    }
    claimStarted.current = true;
    claim.mutate(undefined, { onSuccess: onClaimed });
  }, [current, claim, onClaimed]);

  // Resuelta en contra: la referencia ya no sirve para nada.
  useEffect(() => {
    if (current === 'REJECTED' || current === 'EXPIRED') {
      clearRebindRequest();
    }
  }, [current]);

  function requestAgain(): void {
    clearRebindRequest();
    onRequestAgain();
  }

  const backButton = (
    <Button variant="secondary" onClick={onBack}>
      {t('common.buttons.back')}
    </Button>
  );

  if (current === 'REJECTED') {
    return (
      <Screen
        data-testid="rebind-status"
        align="center"
        title={t('access.rebind.status.rejectedTitle')}
        lead={t('access.rebind.status.contactAdmin')}
        footer={backButton}
      />
    );
  }

  if (current === 'EXPIRED') {
    return (
      <Screen
        data-testid="rebind-status"
        align="center"
        title={t('access.rebind.status.expiredTitle')}
        lead={t('access.rebind.status.expiredLead')}
        footer={
          <>
            <Button onClick={requestAgain}>{t('access.rebind.status.requestAgain')}</Button>
            {backButton}
          </>
        }
      />
    );
  }

  if (current === 'APPROVED') {
    if (claim.isError) {
      return (
        <Screen
          data-testid="rebind-status"
          align="center"
          title={t('access.rebind.status.claimFailedTitle')}
          lead={claimErrorMessage(claim.error)}
          footer={
            <>
              <Button onClick={requestAgain}>{t('access.rebind.status.requestAgain')}</Button>
              {backButton}
            </>
          }
        />
      );
    }
    return (
      <WaitingScreen
        label={t('access.rebind.status.claimingSpinner')}
        title={t('access.rebind.status.approvedTitle')}
        lead={t('access.rebind.status.claiming')}
      />
    );
  }

  if (status.isError) {
    return (
      <Screen
        data-testid="rebind-status"
        align="center"
        title={t('access.rebind.status.errorTitle')}
        lead={t('access.rebind.status.error')}
        footer={
          <>
            <Button onClick={() => void status.refetch()}>{t('common.buttons.retry')}</Button>
            {backButton}
          </>
        }
      />
    );
  }

  return (
    <WaitingScreen
      label={t('access.rebind.status.pendingSpinner')}
      title={t('access.rebind.status.pendingTitle')}
      lead={t('access.rebind.status.pendingLead')}
      footer={backButton}
    />
  );
}
